/**
 * The patient overview (PHASE-0.md §2.4).
 *
 * **One screen, no tabs.** The order is §2.4's order: identity and safety flags, then
 * current medications, then the latest notes. Nothing is behind a click, because a fact
 * behind a click is a fact nobody has read.
 *
 * **The identity block is sticky and deliberately uncapped.** `AGENTS.md` §5 requires
 * allergies, DNR, treatment limitation and weight readable without scrolling, and §2.4
 * adds isolation — so the five slots are the first thing on the screen and they stay
 * there while the notes scroll under them. A patient with eight allergies gets a taller
 * block and pushes the medication table down, which is the right way round: §5 says that
 * if a layout change pushes these below the fold, the layout change is wrong. A
 * `max-height` with an inner scrollbar would be exactly that change, dressed up.
 *
 * **What can be written here, and what cannot, is said rather than discovered.** No new
 * ordination and no administrering in phase 0 (§2.4, §4). What *can* be written is a note,
 * a correction to the identity (DECISIONS.md #43), an allergy or safety flag, and a
 * correction to an existing order's dosage (#46) — and the sentence in the bar says so,
 * rather than leaving a clinician to find out by hunting for a button.
 *
 * **The affordances for #46 are outside the sticky block, deliberately.** Not one *Ändra*
 * inside a safety slot: above the fold is a budget, the five slots already overrun it on a
 * 1366×768 screen, and a button per row buys discoverability with the one thing §5 says may
 * not be traded. So the panel above is unchanged and the section below lists the same
 * entries with a way into each.
 *
 * One request, one audit row. There is no refresh timer: see `fetchPatientOverview`.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { Session } from '../auth/authApi'
import { mayNotDocumentMessage } from '../auth/authDisplay'
import { formatClinicalDateOnly, formatClinicalDateTime } from '../format/clinicalDate'
import { NoteEditor } from '../notes/NoteEditor'
import { NoteVersionHistory } from '../notes/NoteVersionHistory'
import { AllergyForm } from './AllergyForm'
import { MedicationCorrectionForm } from './MedicationCorrectionForm'
import { PatientCorrectionScreen } from './PatientCorrection'
import { PatientTransferForm } from './PatientTransferForm'
import { DeathForm } from './DeathForm'
import { ForensicMarkerBadge, ForensicMarkerButton } from './ForensicMarkerControl'
import { deathBanner } from './deathDisplay'
import { PatientVersionHistory } from './PatientVersionHistory'
import { SafetyFlagForm } from './SafetyFlagForm'
import { correctionMode, correctionOpenLabel } from './correctionDisplay'
import type { MedicationTranslate } from './medicationDisplay'
import {
  guarded,
  patientAge,
  patientName,
  safetyBadges,
} from './patientDisplay'
import {
  fetchPatientOverview,
  PatientOverviewError,
  type PatientMedication,
  type PatientNote,
  type PatientOverview,
  type PatientOverviewFailure,
} from './patientOverviewApi'
import {
  allergyLine,
  flagLabel,
  identifierLabel,
  medicationDose,
  medicationPreparation,
  medicationSchedule,
  noteAttribution,
  noteRecordedLater,
  noteLanguageName,
  noteTypeLabel,
  overviewSafety,
  patientSex,
  practitionerName,
  safetyPanel,
  type PatientOverviewTranslate,
  type SafetyPanelEntry,
} from './patientOverviewDisplay'
import { ExportError, fetchFhirExport, saveFile, type ExportFailure } from './exportApi'
import { patientLocation } from './patientSearchDisplay'
import { pinnedScrollPadding } from './pinnedScrollPadding'
import type { NoteTranslate } from '../notes/noteDisplay'
import type { PrintDocument } from '../print/printApi'

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly overview: PatientOverview }
  | { readonly kind: 'failed'; readonly failure: PatientOverviewFailure }

/** One labelled fact in the identity block. */
function Fact({
  label,
  children,
}: {
  readonly label: string
  readonly children: ReactNode
}) {
  return (
    <div className="patient__fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}

/**
 * One of the safety slots.
 *
 * The rows are a list even when there is one of them, because the record permits more
 * than one flag of a type and a panel that shows the first of two treatment limitations
 * is a panel that hides a decision.
 */
function SafetySlot({ entry }: { readonly entry: SafetyPanelEntry }) {
  return (
    <div
      className={`patient-safety__slot patient-safety__slot--${entry.level}${
        // A neutral slot the record says nothing about — no DNR decision, no limitation, no
        // isolation documented. Quiet, so the slots that apply are found first.
        entry.level === 'neutral' && !entry.documented ? ' patient-safety__slot--quiet' : ''
      }`}
    >
      <dt>{entry.label}</dt>
      <dd>
        <ul className="patient-safety__rows">
          {entry.rows.map((row, index) => (
            // The index is the key because a row has no identity of its own: the flags
            // behind it are versioned rows whose `record_id` the API does not send, and
            // two flags of a type can carry the same brief.
            <li key={index}>
              <span className="patient-safety__value">{row.value}</span>
              {row.detail !== null && (
                <span className="patient-safety__detail">{row.detail}</span>
              )}
              {row.attribution !== null && (
                <span className="patient-safety__attribution">{row.attribution}</span>
              )}
            </li>
          ))}
        </ul>
      </dd>
    </div>
  )
}

/**
 * Resolves the entry a form was opened on, or says the record no longer holds it.
 *
 * **The alternative was worse.** A `find` that fell back to null would turn a correction into a
 * *create* the moment the row went missing — a second allergy for the same substance, written
 * silently, in a table with no `DELETE` (AGENTS.md §3). This cannot happen from the buttons above,
 * which read the ids out of the overview that is on screen; it can happen once the overview has
 * refetched under an open form, which is exactly the case `onStale` creates.
 *
 * The message is the server's own *not found* wording, so a clinician who hits it here reads the
 * same sentence they would have read from the refusal.
 *
 * @param recordId The `record_id` the form was opened on, or null to document a new entry.
 * @param entries What the overview currently holds.
 * @param identity Reads an entry's `record_id`.
 * @param missing The wording for an entry the record no longer holds.
 * @param children Renders the form, given the entry or null.
 */
function SafetyFormSlot<Entry>({
  recordId,
  entries,
  identity,
  missing,
  children,
}: {
  readonly recordId: string | null
  readonly entries: readonly Entry[]
  readonly identity: (entry: Entry) => string
  readonly missing: string
  readonly children: (entry: Entry | null) => ReactNode
}) {
  if (recordId === null) {
    return <>{children(null)}</>
  }

  const found = entries.find((entry) => identity(entry) === recordId)

  if (found === undefined) {
    return (
      <p className="patient__notice patient__notice--error" role="alert">
        {missing}
      </p>
    )
  }

  return <>{children(found)}</>
}

function MedicationRow({
  medication,
  canDocument,
  onCorrect,
  t,
}: {
  readonly medication: PatientMedication

  /** Whether the signed-in role may write. Decides the *Ändra* cell, not the row. */
  readonly canDocument: boolean

  readonly onCorrect: (medicationId: string) => void
  readonly t: MedicationTranslate
}) {
  // A local, so the null check below narrows for real. A cast inside the JSX would be
  // asserting what the check already proves, and would keep proving it after someone
  // changes the check.
  const endAt = medication.endAt

  return (
    <tr>
      <td className="patient-medications__substance">{medication.substance}</td>
      <td>{medicationPreparation(medication, t)}</td>

      {/* The dose, and the only cell on this screen that can refuse to render: the
          shared formatter throws rather than print something that could be misread
          (AGENTS.md §5), and `medicationDose` contains that to this cell. */}
      <td className="patient-medications__dose">{medicationDose(medication, t)}</td>

      <td>{medication.route}</td>
      <td>{medicationSchedule(medication, t)}</td>
      <td>{medication.indication}</td>
      <td className="patient-medications__ordered">
        <span>{guarded(() => formatClinicalDateTime(medication.startAt), t)}</span>
        <span className="patient-medications__prescriber">
          {t('patient:medication.orderedBy', {
            name: practitionerName(
              medication.prescriberGivenName,
              medication.prescriberFamilyName,
              t,
            ),
          })}
        </span>
        {endAt !== null && (
          <span className="patient-medications__ends">
            {t('patient:medication.endsAt', {
              time: guarded(() => formatClinicalDateTime(endAt), t),
            })}
          </span>
        )}
      </td>

      {/* The last column rather than the first: the dose is what a clinician came to the table to
          read, and a control in front of it is a control in the way. */}
      <td className="patient-medications__correct">
        {/* Only above version 1, for the reason the identity marker gives. It sits beside the button
            rather than in the dose cell, so nothing this adds can push the dose onto a second
            line. */}
        {medication.version > 1 && (
          <span className="patient-medications__version">
            {t('patient:history.number', { version: medication.version })}
          </span>
        )}

        {canDocument && (
          <button
            type="button"
            aria-label={t('medication:screen.openNamed', { substance: medication.substance })}
            onClick={() => onCorrect(medication.medicationId)}
          >
            {t('medication:screen.open')}
          </button>
        )}
      </td>
    </tr>
  )
}

function Note({
  note,
  patientId,
  uiLanguage,
  versionsShown,
  canDocument,
  onAmend,
  onToggleVersions,
  onMarkerChanged,
  t,
}: {
  readonly note: PatientNote
  readonly patientId: string
  readonly uiLanguage: string

  /** Whether this note's history is open. Fetched only while it is — see `NoteVersionHistory`. */
  readonly versionsShown: boolean

  /**
   * Whether to offer the correction button.
   *
   * A correction is a clinical write like any other — a new signed version — so a role that
   * may not document may not correct either. The history button stays on regardless: reading
   * is not writing, and a nurse who may not document still needs to see whether a note has
   * been corrected.
   */
  readonly canDocument: boolean

  readonly onAmend: (noteId: string) => void
  readonly onToggleVersions: (noteId: string) => void

  /** A forensic marker was set or removed, or the attempt was refused: the overview refetches. */
  readonly onMarkerChanged: () => void

  /** Wider than {@link PatientOverviewTranslate}: the actions are the note feature's own. */
  readonly t: NoteTranslate
}) {
  const recordedLater = noteRecordedLater(note, t)

  // The note's own language, on the element that holds its text. Correct HTML and it is
  // what lets a screen reader pronounce a Swedish note read under an English interface.
  // Never a reason to translate the content (AGENTS.md §3 rule 8).
  const sections = [
    { key: 'subjective', label: t('patient:notes.subjective'), text: note.subjective },
    { key: 'objective', label: t('patient:notes.objective'), text: note.objective },
    { key: 'assessment', label: t('patient:notes.assessment'), text: note.assessment },
    { key: 'plan', label: t('patient:notes.plan'), text: note.plan },
  ].filter((section) => section.text !== null && section.text !== '')

  return (
    <article className="patient-note">
      <header className="patient-note__header">
        <h3 className="patient-note__type">{noteTypeLabel(note.noteType, t)}</h3>
        <p className="patient-note__by">{noteAttribution(note, t)}</p>

        {/* Retroactive documentation is normal, not an edge case (AGENTS.md §4). Shown
            only when a reader would see two different times — see `noteRecordedLater`. */}
        {recordedLater !== null && (
          <p className="patient-note__recorded">{recordedLater}</p>
        )}

        {/* A corrected note that does not say it was corrected is the thing an
            append-only record exists to prevent. */}
        {note.version > 1 && (
          <p className="patient-note__amended">
            {t('patient:notes.amended', { version: note.version })}
          </p>
        )}

        {note.language !== uiLanguage && (
          <p className="patient-note__language">
            {t('patient:notes.otherLanguage', {
              language: noteLanguageName(note.language, uiLanguage),
            })}
          </p>
        )}

        {/* DECISIONS.md #53 step 2. Shown to everybody who reads the note. */}
        <ForensicMarkerBadge note={note} />
      </header>

      <div className="patient-note__body" lang={note.language}>
        {sections.length > 0 && (
          <dl className="patient-note__soap">
            {sections.map((section) => (
              <div key={section.key}>
                <dt>{section.label}</dt>
                <dd>{section.text}</dd>
              </div>
            ))}
          </dl>
        )}
        {note.bodyText !== null && note.bodyText !== '' && (
          <p className="patient-note__text">{note.bodyText}</p>
        )}
      </div>

      <footer className="patient-note__actions">
        {/* "Correct", not "edit". Nothing here modifies the note: a correction is a new
            version and the one on screen stays in the record (AGENTS.md §3 rule 2), and the
            word on the button is the first place that has to say so.

            Withheld from a role that may not document, rather than shown and then refused —
            a correction is typed against a note somebody is reading, and finding out at the
            signing step means losing it. */}
        {canDocument && (
          <button type="button" onClick={() => onAmend(note.noteId)}>
            {t('notes:action.amend')}
          </button>
        )}

        {/* A role that may document may mark, the same rule as the server's gate (#53). */}
        {canDocument && (
          <ForensicMarkerButton note={note} patientId={patientId} onChanged={onMarkerChanged} />
        )}

        {/* Offered on every note, not only on a corrected one. Whether a note has been
            corrected is exactly what a reader may want to check, and a button that appears
            only when the answer is yes answers it before it is pressed — on the screen
            beside the text, where a reader who has not noticed the badge would not connect
            the two. */}
        <button
          type="button"
          aria-expanded={versionsShown}
          onClick={() => onToggleVersions(note.noteId)}
        >
          {versionsShown ? t('notes:action.hideVersions') : t('notes:action.showVersions')}
        </button>
      </footer>

      {versionsShown && <NoteVersionHistory patientId={patientId} noteId={note.noteId} />}
    </article>
  )
}

export function PatientOverviewScreen({
  patientId,
  session,
  onBack,
  onPrint,
}: {
  readonly patientId: string

  /**
   * Who is signed in, for `canDocument` and for the message shown in the note form's place.
   *
   * The whole session rather than the boolean, because the message names the role and the two
   * must not be able to disagree — a screen saying "you may not document as physician" would
   * be worse than one that said nothing.
   */
  readonly session: Session

  readonly onBack: () => void

  /**
   * Opens one of the two printed documents (PHASE-0.md §2.6).
   *
   * A callback rather than a link, because this screen does not know where it sits in the
   * app — and because arriving at the print view writes an `audit.event` row with
   * `action = 'print'`. That has to be something a clinician did on purpose, not something
   * a stray navigation can do on their behalf.
   */
  readonly onPrint: (documentKind: PrintDocument) => void
}) {
  // `common` first: it is the default namespace and it is what lets this screen's `t`
  // render a safety badge through patientDisplay.ts. `notes` and `print` after it, for the
  // note actions and the two print buttons — the wider `t` is still assignable to
  // PatientOverviewTranslate everywhere else.
  // `auth` last, for the one message shown where the note form would be.
  // `registration` and `correction` for the correction form, which borrows the registration
  // form's field wording rather than holding a second copy of it (see `correctionDisplay.ts`).
  // `safety` and `medication` last, for #46's three forms and the two sections that open them.
  const { t, i18n } = useTranslation([
    'common',
    'patient',
    'notes',
    'print',
    'auth',
    'registration',
    'correction',
    'safety',
    'medication',
    'transfer',
    'death',
  ])
  const [state, setState] = useState<State>({ kind: 'loading' })

  // Bumped by the retry button, so a retry is a new attempt at the same patient rather
  // than an imperative refetch to keep in step with the state.
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => {
    setState({ kind: 'loading' })
    setAttempt((value) => value + 1)
  }, [])

  /**
   * Refetch without blanking the screen.
   *
   * What a signed note needs, and the difference from {@link retry} is the whole point: the
   * editor is mounted inside this screen, so going through `loading` would unmount it and
   * take the confirmation that the note was signed with it. Here the old overview stays up
   * until the new one arrives and the note appears underneath the confirmation.
   */
  const refresh = useCallback(() => setAttempt((value) => value + 1), [])

  /**
   * The open editor, or null.
   *
   * `session` only exists to be the editor's `key`: opening the editor again must be a
   * fresh component, because its draft is initialised once on mount and never reset (see
   * `NoteEditor`). Without it, correcting one note and then another would keep the first
   * note's text.
   */
  const [editing, setEditing] = useState<{
    readonly amendingNoteId: string | null
    readonly session: number
  } | null>(null)

  /** Opens the editor on a new note, or on a correction. Always a fresh `session`. */
  const openEditor = useCallback((amendingNoteId: string | null) => {
    setEditing((previous) => ({
      amendingNoteId,
      session: (previous?.session ?? 0) + 1,
    }))
  }, [])

  /**
   * Whether the correction form is open (DECISIONS.md #43).
   *
   * No `session` counter, unlike {@link editing}: there is one identity per screen, so closing
   * the form unmounts it and opening it again is a fresh component that reads the draft from
   * whatever the record says by then.
   */
  const [correcting, setCorrecting] = useState(false)

  /**
   * Whether the transfer form is open (DECISIONS.md #48). A plain flag for the reason
   * {@link correcting} is one: one placement per screen, so reopening is a fresh form that starts
   * from wherever the record says the patient is by then.
   */
  const [moving, setMoving] = useState(false)

  /** Whether the death form is open (DECISIONS.md #53). */
  const [recordingDeath, setRecordingDeath] = useState(false)

  /**
   * The open allergy or safety-flag form, or null (DECISIONS.md #46).
   *
   * One piece of state for both, because only one may be open: two unsaved forms on a screen with
   * no draft storage (#38) is two ways to lose the wrong one, which is the reason the note editor
   * hides its own button while it is open.
   *
   * **`session` is what makes switching targets safe**, for the same reason {@link editing} carries
   * one: a form initialises its draft once on mount, so going from one allergy straight to another
   * without a remount would carry the first one's substance into the second one's box — in a form
   * whose Save cannot be undone.
   */
  const [safetyForm, setSafetyForm] = useState<
    | {
        readonly kind: 'allergy'
        /** The `record_id` being corrected, or null to document one. */
        readonly allergyId: string | null
        readonly session: number
      }
    | {
        readonly kind: 'flag'
        readonly flagId: string | null
        readonly session: number
      }
    | null
  >(null)

  const openAllergyForm = useCallback((allergyId: string | null) => {
    setSafetyForm((previous) => ({
      kind: 'allergy',
      allergyId,
      session: (previous?.session ?? 0) + 1,
    }))
  }, [])

  const openFlagForm = useCallback((flagId: string | null) => {
    setSafetyForm((previous) => ({
      kind: 'flag',
      flagId,
      session: (previous?.session ?? 0) + 1,
    }))
  }, [])

  /**
   * The order whose dosage is being corrected, or null (DECISIONS.md #46).
   *
   * The `record_id`, not the row: after a save the overview refetches and the order comes back as a
   * new version, and a form keyed on the version would unmount underneath its own confirmation.
   */
  const [correctingMedicationId, setCorrectingMedicationId] = useState<string | null>(null)

  /**
   * Whether the identity history is open (DECISIONS.md #45).
   *
   * Starts closed, and closed is the only safe default: opening it reads the record and writes an
   * audit row that can never be deleted (AGENTS.md §4). A history that came up with the screen
   * would leave the log saying every clinician who opened a patient asked to see how the identity
   * got there, which is a claim about all of them that is true of almost none.
   */
  const [historyShown, setHistoryShown] = useState(false)

  /**
   * The FHIR export (DECISIONS.md #52): idle, in flight, or failed with why. Not a remembered
   * success — the file is in the browser's downloads, and the audit row is in the log.
   */
  const [exporting, setExporting] = useState<
    { readonly kind: 'idle' } | { readonly kind: 'working' } | { readonly kind: 'failed'; readonly failure: ExportFailure }
  >({ kind: 'idle' })

  const exportRecord = () => {
    setExporting({ kind: 'working' })
    fetchFhirExport(patientId, new AbortController().signal).then(
      (file) => {
        saveFile(file)
        setExporting({ kind: 'idle' })
      },
      (cause: unknown) => {
        setExporting({
          kind: 'failed',
          failure: cause instanceof ExportError ? cause.failure : 'unavailable',
        })
      },
    )
  }

  /**
   * Which notes have their history open.
   *
   * Opening one fetches it, and that fetch writes an audit row (AGENTS.md §4) — so this
   * starts empty and nothing is read until a clinician asks.
   */
  const [shownVersions, setShownVersions] = useState<ReadonlySet<string>>(() => new Set())

  const toggleVersions = useCallback((noteId: string) => {
    setShownVersions((previous) => {
      const next = new Set(previous)
      if (!next.delete(noteId)) {
        next.add(noteId)
      }
      return next
    })
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    fetchPatientOverview(patientId, controller.signal).then(
      (overview) => {
        if (!controller.signal.aborted) {
          setState({ kind: 'loaded', overview })
        }
      },
      (cause: unknown) => {
        if (controller.signal.aborted) {
          return
        }
        setState({
          kind: 'failed',
          failure: cause instanceof PatientOverviewError ? cause.failure : 'unavailable',
        })
      },
    )

    return () => controller.abort()
  }, [patientId, attempt])

  // The base subtag, so a note written in `sv` is not marked as foreign under `sv-SE`.
  const uiLanguage = (i18n.resolvedLanguage ?? i18n.language).replace(/-.*$/, '')

  if (state.kind !== 'loaded') {
    return (
      <main className="patient">
        <div className="patient__bar">
          <button type="button" className="patient__back" onClick={onBack}>
            {t('patient:overview.back')}
          </button>
        </div>

        <div aria-live="polite">
          {state.kind === 'loading' && (
            <p className="patient__notice">{t('patient:overview.loading')}</p>
          )}

          {state.kind === 'failed' && (
            <div className="patient__notice patient__notice--error" role="alert">
              <p>
                {state.failure === 'not-found'
                  ? t('patient:overview.notFound')
                  : t('patient:overview.failed')}
              </p>

              {/* No retry for a patient who is not in the record: the id is wrong, or
                  the record was retracted, and asking again teaches nothing. */}
              {state.failure === 'unavailable' && (
                <button type="button" onClick={retry}>
                  {t('patient:overview.retry')}
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    )
  }

  const { overview } = state
  const today = new Date()
  const location = overview.location
  const birthDate = overview.birthDate

  return (
    <main className="patient">
      {/*
        Sticky and uncapped — see this file's header. `top` is the banner's height
        because the banner is the one thing that must never be covered (index.css).
        Measured, so that a form's autofocus scrolls its field below this block rather
        than under it — see `pinnedScrollPadding`.
      */}
      <div className="patient__identity" ref={pinnedScrollPadding}>
        <div className="patient__bar">
          <button type="button" className="patient__back" onClick={onBack}>
            {t('patient:overview.back')}
          </button>

          {/*
            Three buttons and no print menu — §2.6's two documents and #51's journalutdrag — and a
            clinician holding a printout needs to have chosen which one — each is audited
            as `print` the moment the view opens (see `PrintScreen`), and the log cannot be
            corrected afterwards.
          */}
          <div className="patient__print">
            <button type="button" onClick={() => onPrint('administration-record')}>
              {t('print:action.administrationRecord')}
            </button>
            <button type="button" onClick={() => onPrint('wristband')}>
              {t('print:action.wristband')}
            </button>
            {/* The whole record on paper, every signed note included (DECISIONS.md #51). */}
            <button type="button" onClick={() => onPrint('record-extract')}>
              {t('print:action.recordExtract')}
            </button>
            {/* The record as a file (DECISIONS.md #52). Only for a role that may document, which
                is what the server gates it on — a file is more portable than paper. */}
            {session.canDocument && (
              <button
                type="button"
                disabled={exporting.kind === 'working'}
                title={t('patient:export.notEncrypted')}
                onClick={exportRecord}
              >
                {exporting.kind === 'working' ? t('patient:export.working') : t('patient:export.action')}
              </button>
            )}
          </div>
        </div>

        <div className="patient__heading">
          <h1 className="patient__name">{patientName(overview, t)}</h1>

          {/* Every identifier, not the one that matched a search: a patient who arrived
              unidentified and later acquired a personnummer holds both, and the
              reservnummer is what the wristband and yesterday's notes say. */}
          <ul className="patient__identifiers">
            {overview.identifiers.length === 0 ? (
              <li>{t('patient:identifier.none')}</li>
            ) : (
              overview.identifiers.map((identifier) => (
                <li key={`${identifier.system}-${identifier.value}`}>
                  <span className="patient__identifier-label">
                    {identifierLabel(identifier.system, t)}
                  </span>{' '}
                  {identifier.assigner === null
                    ? identifier.value
                    : t('patient:identifier.withAssigner', {
                        value: identifier.value,
                        assigner: identifier.assigner,
                      })}
                </li>
              ))
            )}
          </ul>

          {/* The same badges as a ward-list row and a search hit, from the same
              function: an allergy must not read one way in a list and another here
              (PHASE-0.md §3.4). */}
          <ul className="badges">
            {safetyBadges(overviewSafety(overview, t), t).map((badge) => (
              <li key={badge.key} className={`badge badge--${badge.level}`}>
                {badge.label}
              </li>
            ))}
          </ul>
        </div>

        <dl className="patient__facts">
          <Fact label={t('patient:overview.birthDate')}>
            {birthDate === null
              ? t('common:value.notRecorded')
              : guarded(() => formatClinicalDateOnly(birthDate), t)}
          </Fact>
          <Fact label={t('patient:overview.age')}>
            {guarded(() => patientAge(overview, t, today), t)}
          </Fact>
          <Fact label={t('patient:overview.sex')}>{patientSex(overview.sex, t)}</Fact>

          {/* The ward's name and the bed are two facts with two labels, not one string:
              the code is what staff say and what the printed sheet carries, and the name
              is what tells a clinician from another department where M3 is. */}
          <Fact label={t('patient:overview.ward')}>
            {location?.wardName ?? t('common:value.notRecorded')}
          </Fact>
          <Fact label={t('patient:overview.location')}>
            {patientLocation(
              { ward: location?.ward ?? null, bed: location?.bed ?? null },
              t,
            )}
          </Fact>
          <Fact label={t('patient:overview.admitted')}>
            {location === null
              ? t('common:value.notRecorded')
              : guarded(() => formatClinicalDateTime(location.admittedAt), t)}
          </Fact>
          <Fact label={t('patient:overview.responsible')}>
            {location === null
              ? t('common:value.notRecorded')
              : practitionerName(
                  location.responsibleGivenName,
                  location.responsibleFamilyName,
                  t,
                )}
          </Fact>
        </dl>

        {/* A recorded death, above the strip and filled like a present hazard (DECISIONS.md #53). The
            five slots stay as they are underneath: the patient is still in the bed and the record is
            still read. One paragraph, not a list, so it costs the pinned block as little height as
            the facts allow. */}
        {overview.death !== null && (
          <section className="patient-death" aria-label={t('death:screen.sectionHeading')}>
            {(() => {
              const [died, ...rest] = deathBanner(overview.death, t)
              return (
                <p className="patient-death__lines">
                  <strong>{died}</strong>
                  {/* By position, not by text: the circumstances are free text and could repeat a
                      line, and two equal keys would let React drop one. */}
                  {rest.map((line, index) => (
                    <span key={index} className="patient-death__line">
                      {line}
                    </span>
                  ))}
                </p>
              )
            })()}
          </section>
        )}

        <section className="patient-safety" aria-label={t('patient:overview.safetyHeading')}>
          <dl className="patient-safety__slots">
            {safetyPanel(overview, t).map((entry) => (
              <SafetySlot key={entry.key} entry={entry} />
            ))}
          </dl>
        </section>
      </div>

      {/* The scope notice, under the pinned block rather than in it. It is the same two lines on
          every patient and says nothing about this one, so pinning it cost a third of a 768 px
          screen's scrolling area on every scroll (UX pass, 2026-10-08). Said once, at the top of
          what scrolls. */}
      <p className="patient__readonly patient__scope">{t('patient:overview.readOnly')}</p>

      {/* A failed export, said under the pinned block rather than in it, where a message would
          make the header taller on the one patient it concerns. */}
      {exporting.kind === 'failed' && (
        <p className="patient__notice patient__notice--error" role="alert">
          {t(`patient:export.${exporting.failure === 'not-found' ? 'notFound' : exporting.failure}`)}
        </p>
      )}

      {/* What a clinician reads on almost every visit comes first under the pinned block: the
          orders, then the notes. The sections that document and correct follow them (UX pass,
          2026-10-08) — a missing allergy is already shouted above the fold by the panel's
          *Allergi ej dokumenterad*, so the way to document it does not also need to be the first
          thing below it. */}
      <section className="patient__section patient-medications">
        <h2>{t('patient:overview.medicationsHeading')}</h2>

        {/* Above the table, where the clinician's attention is once they have pressed the button,
            and because the dose they are typing must not be somewhere they have to scroll to find.
            Resolved against the overview that is on screen: after a save the order comes back as a
            new version under the same `record_id`, which is why the state holds the id and not the
            row.

            `canDocument` guarded here as well as on every button that opens it. */}
        {session.canDocument &&
          correctingMedicationId !== null &&
          (() => {
            const medication = overview.medications.find(
              (candidate) => candidate.medicationId === correctingMedicationId,
            )

            // Gone from the current list — retracted or stopped by somebody else while the form was
            // open. The server's own wording, and no fallback to a blank form: there is no such thing
            // as correcting an order the record does not have.
            if (medication === undefined) {
              return (
                <p className="patient__notice patient__notice--error" role="alert">
                  {t('medication:refusal.notFound')}
                </p>
              )
            }

            return (
              <MedicationCorrectionForm
                // A fresh component per order, for the reason `safetyForm.session` exists: the draft
                // is initialised once on mount, and going from one order to another without a
                // remount would carry the first one's dose into the second one's box.
                key={medication.medicationId}
                patientId={patientId}
                medication={medication}
                onCorrected={refresh}
                onStale={refresh}
                onClose={() => setCorrectingMedicationId(null)}
              />
            )
          })()}

        {overview.medications.length === 0 ? (
          <p className="patient__notice">{t('patient:overview.noMedications')}</p>
        ) : (
          <table className="record-table">
            <caption className="visually-hidden">{t('patient:medication.caption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('patient:medication.columnSubstance')}</th>
                <th scope="col">{t('patient:medication.columnPreparation')}</th>
                <th scope="col">{t('patient:medication.columnDose')}</th>
                <th scope="col">{t('patient:medication.columnRoute')}</th>
                <th scope="col">{t('patient:medication.columnSchedule')}</th>
                <th scope="col">{t('patient:medication.columnIndication')}</th>
                <th scope="col">{t('patient:medication.columnOrdered')}</th>

                {/* A heading the column needs and a sighted reader does not: the cell holds a button
                    that names itself. Empty would leave a screen reader announcing the row's last
                    cell as belonging to *Ordinerat*. */}
                <th scope="col" className="visually-hidden">
                  {t('patient:medication.columnCorrect')}
                </th>
              </tr>
            </thead>
            <tbody>
              {overview.medications.map((medication) => (
                <MedicationRow
                  key={medication.medicationId}
                  medication={medication}
                  canDocument={session.canDocument}
                  onCorrect={setCorrectingMedicationId}
                  t={t}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="patient__section patient-notes">
        <h2>{t('patient:overview.notesHeading')}</h2>

        {/* A role that may not document is told why, in place of the button.

            Not simply nothing: a clinician who cannot find the way to write a note has no way
            to tell "this system does not do that" from "my account does not". The second one
            is fixable by a phone call, and the message is what makes it askable. */}
        {!session.canDocument && (
          <p className="patient__notice patient-notes__refused" role="status">
            {mayNotDocumentMessage(session, t)}
          </p>
        )}

        {/* With no notes, the empty state comes first and the action after it — the order every
            other section on this screen uses (UX pass, 2026-10-08). With notes, the button stays
            above them, for the reason the editor below gives. */}
        {overview.notes.length === 0 && (
          <p className="patient__notice">{t('patient:overview.noNotes')}</p>
        )}

        {/* Hidden while an editor is open, so there is never a second one. Two drafts on one
            screen, neither of them saved anywhere (DECISIONS.md #38), is two ways to lose
            the wrong one. */}
        {session.canDocument && editing === null && (
          <button
            type="button"
            className="patient-notes__write"
            onClick={() => openEditor(null)}
          >
            {t('notes:action.write')}
          </button>
        )}

        {/* Above the notes, because that is where the clinician's attention is once they
            have pressed the button, and because the text they are typing must not be
            somewhere they have to scroll to find. */}
        {/* `canDocument` guarded here as well as on the two buttons that open it. Belt and
            braces on purpose: the editor is the thing that can post a write, and a future
            change that offered a third way in must not be able to reach it. */}
        {session.canDocument && editing !== null && (
          <NoteEditor
            key={editing.session}
            patientId={patientId}
            amendingNoteId={editing.amendingNoteId}
            onWritten={refresh}
            onClose={() => setEditing(null)}
          />
        )}

        {/* Said before the notes, not after them: a clinician who stops reading at the
            oldest visible note has to have been told the list is cut short before they
            conclude it is the first one there is. */}
        {overview.moreNotes && (
          <p className="patient__notice patient__notice--warning" role="status">
            {t('patient:overview.moreNotes', { count: overview.notes.length })}
          </p>
        )}

        {overview.notes.length > 0 &&
          overview.notes.map((note) => (
            <Note
              key={`${note.noteId}-${note.version}`}
              note={note}
              patientId={patientId}
              uiLanguage={uiLanguage}
              versionsShown={shownVersions.has(note.noteId)}
              canDocument={session.canDocument}
              onAmend={openEditor}
              onToggleVersions={toggleVersions}
              // `refresh`, not `retry`: the notes stay on screen while the marker comes back.
              onMarkerChanged={refresh}
              t={t}
            />
          ))}
      </section>

      {/* The way into #46's two forms, and it is here rather than in the panel above for the reason
          this file's header gives: the five slots are the §5 budget, and a button inside a slot
          spends it. The same entries are listed again, which is a duplication worth paying for —
          the panel is what a clinician reads in a hurry and this is what they work in.

          After the orders and the notes since 2026-10-08, and still above the identity section: an
          allergy that is missing from the record is urgent in a way a misspelled first name is
          not. */}
      <section className="patient__section patient-safety-entries">
        <h2>{t('safety:section.heading')}</h2>

        <h3>{t('safety:section.allergiesHeading')}</h3>

        {/* Every allergy the record holds, including the resolved and the retracted ones — unlike
            the panel above, which shows what is current. This is the list you correct from, and a
            row that has been marked `entered-in-error` is exactly the row somebody comes here to
            look for. */}
        {overview.allergies.length === 0 ? (
          <p className="patient__notice">{t('safety:section.noAllergies')}</p>
        ) : (
          <ul className="patient-safety-entries__list">
            {overview.allergies.map((allergy) => (
              <li key={allergy.allergyId}>
                <span className="patient-safety-entries__value">{allergyLine(allergy, t)}</span>

                {/* Only above version 1, for the reason the identity marker gives: `Version 1` on
                    every row teaches a reader to skip the line. */}
                {allergy.version > 1 && (
                  <span className="patient-safety-entries__version">
                    {t('patient:history.number', { version: allergy.version })}
                  </span>
                )}

                {session.canDocument && (
                  <button
                    type="button"
                    // Short on screen, named to a screen reader: five buttons called *Ändra* in one
                    // list are five identical announcements, and the one that matters is which
                    // allergy this one is for.
                    aria-label={t('safety:allergy.openCorrectNamed', {
                      substance: allergyLine(allergy, t),
                    })}
                    onClick={() => openAllergyForm(allergy.allergyId)}
                  >
                    {t('safety:allergy.openCorrect')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Hidden while a form is open, so there is never a second one (#38). */}
        {session.canDocument && safetyForm === null && (
          <button type="button" onClick={() => openAllergyForm(null)}>
            {t('safety:allergy.openDocument')}
          </button>
        )}

        <h3>{t('safety:section.flagsHeading')}</h3>

        {overview.safetyFlags.length === 0 ? (
          <p className="patient__notice">{t('safety:section.noFlags')}</p>
        ) : (
          <ul className="patient-safety-entries__list">
            {overview.safetyFlags.map((flag) => (
              <li key={flag.flagId}>
                <span className="patient-safety-entries__value">
                  {flag.value === null
                    ? flagLabel(flag.flagType, t)
                    : t('patient:safety.otherFlag', {
                        label: flagLabel(flag.flagType, t),
                        // A weight carries its unit here as it does in the slot above
                        // (I18N.md §4, "unit always accompanies the value"). This line said
                        // `Vikt: 62.4` two lines under `62.4 kg` until the browser pass on
                        // 2026-10-07. Same catalogue message as the slot, so they cannot drift.
                        value:
                          flag.flagType === 'weight'
                            ? t('patient:safety.weightKg', { weight: flag.value })
                            : flag.value,
                      })}
                </span>

                {flag.version > 1 && (
                  <span className="patient-safety-entries__version">
                    {t('patient:history.number', { version: flag.version })}
                  </span>
                )}

                {session.canDocument && (
                  <button
                    type="button"
                    aria-label={t('safety:flag.openCorrectNamed', {
                      type: flagLabel(flag.flagType, t),
                    })}
                    onClick={() => openFlagForm(flag.flagId)}
                  >
                    {t('safety:flag.openCorrect')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {session.canDocument && safetyForm === null && (
          <button type="button" onClick={() => openFlagForm(null)}>
            {t('safety:flag.openDocument')}
          </button>
        )}

        {/* `canDocument` guarded here as well as on every button that opens these, for the reason
            the note editor gives: the form is the thing that can post a write, and a future third
            way in must not be able to reach it. */}
        {session.canDocument && safetyForm?.kind === 'allergy' && (
          <SafetyFormSlot
            recordId={safetyForm.allergyId}
            entries={overview.allergies}
            identity={(allergy) => allergy.allergyId}
            missing={t('safety:allergy.refusal.notFound')}
          >
            {(allergy) => (
              <AllergyForm
                key={safetyForm.session}
                patientId={patientId}
                allergy={allergy}
                // Refetch without blanking, so the panel above shows what the record now holds while
                // the confirmation is still up. `refresh` and not `retry`: `loading` would unmount
                // the form and take the confirmation with it.
                onWritten={refresh}
                onStale={refresh}
                onClose={() => setSafetyForm(null)}
              />
            )}
          </SafetyFormSlot>
        )}

        {session.canDocument && safetyForm?.kind === 'flag' && (
          <SafetyFormSlot
            recordId={safetyForm.flagId}
            entries={overview.safetyFlags}
            identity={(flag) => flag.flagId}
            missing={t('safety:flag.refusal.notFound')}
          >
            {(flag) => (
              <SafetyFlagForm
                key={safetyForm.session}
                patientId={patientId}
                flag={flag}
                onWritten={refresh}
                onStale={refresh}
                // #46 point 10: the patient already has a flag of this type and the server named it.
                // Reopening as a correction of that flag is what the clinician was trying to do.
                onCorrectExisting={openFlagForm}
                onClose={() => setSafetyForm(null)}
              />
            )}
          </SafetyFormSlot>
        )}
      </section>

      {/* Below the safety panel and outside the sticky block, on purpose: above the fold is a
          budget (index.css) and correcting a name is the one thing on this screen that is never
          urgent. An allergy must not move down so that a button can sit next to the name. */}
      <section className="patient__section patient-identity">
        <h2>{t('correction:screen.sectionHeading')}</h2>

        {/* The version marker, and it is the way into the history — the same pattern a note's
            marker follows beside `Visa alla versioner` (DECISIONS.md #45). It went absent for the
            length of one afternoon, while #43's write existed and the `GET` on `/versions` did not
            and a marker would have announced a history nobody could open.

            **Only above version 1.** `Version 1` on the great majority of patients is noise that
            teaches a reader to skip the line, and this line is the only thing distinguishing the
            patient whose identity has actually moved.

            Shown to every role, unlike the correction button below: reading the record is not
            documenting in it, and who may read is not the question `canDocument` answers. The read
            writes its own audit row either way. */}
        {overview.version > 1 && (
          <p className="patient-identity__version">
            {t('patient:history.number', { version: overview.version })}
          </p>
        )}

        {/* Outside the `version > 1` guard on purpose: a patient nobody has corrected still has a
            history — one version, written when they were registered or imported — and a clinician
            asking to see it is entitled to the answer rather than to a missing button. What the
            marker above decides is whether the screen volunteers that there is something to look
            at.

            **Not `Visa alla versioner`**, which is the note's wording. Every note below carries a
            button by that name already, so one more would be announced identically to the one on
            yesterday's daily note while opening something else entirely. This wording names what is
            being versioned instead. */}
        <button
          type="button"
          aria-expanded={historyShown}
          onClick={() => setHistoryShown((shown) => !shown)}
        >
          {historyShown ? t('patient:history.hide') : t('patient:history.show')}
        </button>

        {/* Mounted only while it is open, which is what makes the audit row truthful: the fetch
            happens because somebody asked (AGENTS.md §4). Closing and reopening reads again, and
            that is correct — it is a second access. */}
        {historyShown && <PatientVersionHistory patientId={patientId} />}

        {/* Hidden from a role that may not document rather than shown and refused, the same
            choice the note button makes. No second copy of `mayNotDocumentMessage` here: the
            notes section below already says it once on this screen, and twice reads as two
            different refusals. */}
        {session.canDocument && !correcting && (
          <button type="button" onClick={() => setCorrecting(true)}>
            {correctionOpenLabel(correctionMode(overview), t)}
          </button>
        )}

        {/* `canDocument` guarded here as well as on the button, for the reason the note editor
            gives: this is the thing that can post a write. */}
        {session.canDocument && correcting && (
          <PatientCorrectionScreen
            patient={overview}
            // Refetch, so the header above shows the name that is now in the record while the
            // confirmation is still up. `refresh` and not `retry` — going through `loading` would
            // unmount the form and take the confirmation with it.
            onCorrected={refresh}
            onStale={refresh}
            onClose={() => setCorrecting(false)}
          />
        )}
      </section>

      {/* Moving the patient (DECISIONS.md #48). Below the safety panel for the reason the identity
          section above is, and its own section because where a patient lies is not who they are.
          Only for an admitted patient: with no encounter there is no placement to move from, and a
          patient who is in the record and on no ward is registered, not moved. */}
      {location !== null && (
        <section className="patient__section patient-transfer">
          {/* Not `Vårdplats`, which is already the label of the fact in the header above — two
              identical labels for two different things is what a screen reader reads out. */}
          <h2>{t('transfer:screen.sectionHeading')}</h2>

          {/* Hidden from a role that may not document, as the correction button is. */}
          {session.canDocument && !moving && (
            <button type="button" onClick={() => setMoving(true)}>
              {t('transfer:screen.open')}
            </button>
          )}

          {session.canDocument && moving && (
            <PatientTransferForm
              patientId={patientId}
              location={location}
              // `refresh`, not `retry`, for the reason the correction above gives: the confirmation
              // has to survive the refetch that puts the new ward in the header.
              onMoved={refresh}
              onStale={refresh}
              onClose={() => setMoving(false)}
            />
          )}
        </section>
      )}

      {/* Recording a death (DECISIONS.md #53). Last, below everything that is read, and only for a
          role that may establish one — the banner above is what everybody else needs, and a section
          saying "you may not" would be noise on every patient. */}
      {session.canEstablishDeath && (
        <section className="patient__section patient-death-record">
          <h2>{t('death:screen.sectionHeading')}</h2>

          {!recordingDeath && (
            <button type="button" onClick={() => setRecordingDeath(true)}>
              {overview.death === null ? t('death:screen.open') : t('death:screen.openCorrection')}
            </button>
          )}

          {recordingDeath && (
            <DeathForm
              // No key, unlike the medication form: the save refetches and puts a new version here,
              // and a key on it would remount the form and take the confirmation with it. Closing
              // unmounts it, so the next opening starts from the record.
              patientId={patientId}
              death={overview.death}
              onSaved={refresh}
              onStale={refresh}
              onClose={() => setRecordingDeath(false)}
            />
          )}
        </section>
      )}
    </main>
  )
}
